import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { SiteNav, SiteFooter } from "../components/SiteChrome";
import { getPostBySlug, formatPostDate } from "../blog/posts";
import {
  CONTACT_EMAIL,
  CONTACT_PHONE_DISPLAY,
  CONTACT_PHONE_TEL,
  HERO_INNER_MAX,
  POST_BODY_MAX,
  POST_CTA_MARGIN_TOP,
} from "../lib/constants";

function PostBody({ blocks }) {
  return (
    <div className="blog-body editorial" data-testid="blog-post-body">
      {blocks.map((b, i) => {
        const blockKey = `${b.type}-${i}`;
        if (b.type === "h2") {
          return (
            <h2 key={blockKey} data-testid={`blog-h2-${i}`}>
              {b.text}
            </h2>
          );
        }
        if (b.type === "list") {
          return (
            <ul key={blockKey} data-testid={`blog-list-${i}`}>
              {b.items.map((it, j) => (
                <li key={`${blockKey}-item-${j}-${it.slice(0, 24)}`}>{it}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={blockKey} data-testid={`blog-p-${i}`}>
            {b.text}
          </p>
        );
      })}
    </div>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    document.title = post
      ? `${post.title} | HOL Creative Solutions`
      : "Post Not Found | HOL Creative Solutions";
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [post]);

  if (!post) {
    return (
      <div data-testid="blog-post-page-404">
        <SiteNav activeKey="blog" />
        <section className="hol-section">
          <div className="section-label">404</div>
          <h2 className="section-title">Post not found</h2>
          <p className="section-sub">
            That article doesn't exist (or hasn't been published yet).
          </p>
          <Link to="/blog" className="btn btn-green" data-testid="blog-404-back">
            ← Back to Blog
          </Link>
        </section>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page">
      <SiteNav activeKey="blog" />

      {/* HERO */}
      <section className="hero cinematic editorial" data-testid="blog-post-hero">
        <div className="hero-grain" />
        <div className="hero-glow" />
        <div className="hero-inner" style={{ maxWidth: HERO_INNER_MAX.blog }}>
          <div
            className="hero-eyebrow"
            style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}
          >
            <Link
              to="/blog"
              style={{ color: "var(--green-accent)", textDecoration: "none" }}
              data-testid="blog-post-back-to-index"
            >
              ← HOL Field Notes
            </Link>
            <span style={{ opacity: 0.5 }}>·</span>
            <span data-testid="blog-post-category">{post.category}</span>
            <span style={{ opacity: 0.5 }}>·</span>
            <span data-testid="blog-post-date">{formatPostDate(post.date)}</span>
          </div>
          <h1 data-testid="blog-post-title">{post.title}</h1>
          <p className="hero-sub">{post.excerpt}</p>
        </div>
      </section>

      <div className="cinema-stage">
      {/* BODY */}
      <section className="hol-section" data-testid="blog-post-body-section">
        <div style={{ maxWidth: POST_BODY_MAX }}>
          <PostBody blocks={post.body} />

          <div
            style={{
              marginTop: POST_CTA_MARGIN_TOP,
              paddingTop: 32,
              borderTop: "1px solid var(--glass-border)",
              display: "flex",
              flexWrap: "wrap",
              gap: 16,
              alignItems: "center",
              justifyContent: "space-between",
            }}
            data-testid="blog-post-cta"
          >
            <div>
              <p
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "1.3rem",
                  color: "var(--text-fog)",
                  margin: 0,
                }}
              >
                Working on a venture?
              </p>
              <p style={{ fontSize: "0.92rem", color: "var(--text-muted)", margin: "4px 0 0" }}>
                Reach out and tell us what you're building.
              </p>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              <a
                href={`tel:${CONTACT_PHONE_TEL}`}
                className="btn btn-primary"
                data-testid="blog-post-cta-call"
              >
                {CONTACT_PHONE_DISPLAY}
              </a>
              <a
                href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
                  "Inquiry: " + post.title
                )}`}
                className="btn btn-secondary"
                data-testid="blog-post-cta-email"
              >
                Email Us
              </a>
              <Link
                to="/blog"
                className="btn btn-secondary"
                data-testid="blog-post-cta-back"
              >
                ← All articles
              </Link>
            </div>
          </div>
        </div>
      </section>
      </div>

      <SiteFooter />
    </div>
  );
}
